import React, { useContext, useState } from 'react';
import { Badge, Box, Button, Flex, Grid, Icon, Text, Tooltip, useDisclosure } from '@chakra-ui/react';
import { useMutation } from '@apollo/client';
import moment from 'moment';

import { AccessPoint, HubCounter, HIDE_INVENTORY_IN_APP, UNHIDE_INVENTORY_IN_APP } from '../query';
import ChangeAccessPointStatusModal from '../modals/ChangeAccessPointStatus';
import ToggleAccessPointActiveStatusModal from '../modals/ToggleAccessPointActiveStatus';
import { statusColorScheme } from '../../AccessPoints/List/Card';
import { Question } from '@phosphor-icons/react';
import { useSpinner } from '../../../contexts/SpinnerContext';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { get } from 'lodash';
import UserContext from '../../../contexts/UserContext';
import HubCounterPanel, { getEnsurePartnerAccessPointsLabel, shouldShowEnsurePartnerAccessPoints } from './HubCounterPanel';
import EnsurePartnerAccessPointsModal from '../modals/EnsurePartnerAccessPoints';

interface AccessPointTabProps {
  accessPoint?: AccessPoint | null;
  hubCounter?: HubCounter | null;
  refetch: () => Promise<any> | void;
  dropPointId: string;
}

const AccessPointTab = ({ accessPoint, hubCounter, dropPointId, refetch }: AccessPointTabProps) => {
  const { toggleSpinner } = useSpinner();
  const { user } = useContext(UserContext);
  const toast = useCustomToast();
  const [hideInventoryInApp] = useMutation(HIDE_INVENTORY_IN_APP);
  const [unhideInventoryInApp] = useMutation(UNHIDE_INVENTORY_IN_APP);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isToggleOpen, onOpen: onToggleOpen, onClose: onToggleClose } = useDisclosure();
  const ensureDisclosure = useDisclosure();
  const [toggleTargetActive, setToggleTargetActive] = useState(false);
  const showEnsurePartnerAccessPoints = shouldShowEnsurePartnerAccessPoints({ accessPoint, hubCounter });
  const ensurePartnerAccessPointsLabel = getEnsurePartnerAccessPointsLabel({ accessPoint, hubCounter });
  const accessPointID = accessPoint?.accessPointID;
  const status = accessPoint?.status;
  const isActive = accessPoint?.isActive;
  const submitTimestamp = accessPoint?.submitTimestamp;
  const identificationSuccess = accessPoint?.identificationSuccess;
  const identificationSuccessTimestamp = accessPoint?.identificationSuccessTimestamp;
  const rejectionReason = accessPoint?.rejectionReason;
  const rejectionTimestamp = accessPoint?.rejectionTimestamp;
  const na1adlActive = accessPoint?.na1adlActive;
  const na1adlChangedAt = accessPoint?.na1adlChangedAt;

  const handleOpenToggleModal = (targetActive: boolean) => {
    setToggleTargetActive(targetActive);
    onToggleOpen();
  };

  const handleHideInventoryInApp = async () => {
    try {
      toggleSpinner(true);
      await hideInventoryInApp({ variables: { accessPointID, internalUserId: user.id } });
      toast({
        title: 'Erfolg!',
        description: 'Inventar wurde in der App ausgeblendet.',
        status: 'success',
      });
    } catch (error) {
      console.log(error);
      toast({
        description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
        status: 'error',
      });
    } finally {
      toggleSpinner(false);
    }
  };

  const handleUnhideInventoryInApp = async () => {
    try {
      toggleSpinner(true);
      await unhideInventoryInApp({ variables: { accessPointID, internalUserId: user.id } });
      toast({
        title: 'Erfolg!',
        description: 'Inventar wurde in der App eingeblendet.',
        status: 'success',
      });
    } catch (error) {
      console.log(error);
      toast({
        description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
        status: 'error',
      });
    } finally {
      toggleSpinner(false);
    }
  };

  return (
    <>
      <Grid
        borderLeftWidth="1px"
        borderRightWidth="1px"
        borderBottomWidth="1px"
        templateColumns={['1fr', '1fr 1fr']}
        gridGap="8"
        px="4"
        py="8"
      >
        {showEnsurePartnerAccessPoints ? (
          <Box gridColumn="1 / -1" p="3" bg="orange.50" borderRadius="md">
            <Text fontSize="xs" fontWeight="semibold" color="orange.700" mb="1">
              Partner-Datensätze unvollständig
            </Text>
            <Text fontSize="sm" color="orange.800">
              Für diesen DropPoint fehlt mindestens einer der benötigten UPS- oder Amazon-Einträge. Über &quot;
              {ensurePartnerAccessPointsLabel}&quot; werden nur die fehlenden Datensätze ergänzt.
            </Text>
          </Box>
        ) : null}
        <Box>
          <Text fontWeight="semibold" fontSize="sm" mb="3">
            UPS Support
          </Text>
          {accessPoint ? (
            <>
              {accessPointID ? (
                <Flex alignItems="center" gap="4">
                  <Text fontSize="sm">Access Point ID:</Text>
                  <Text fontSize="sm" fontWeight="bold">
                    {accessPointID}
                  </Text>
                </Flex>
              ) : null}
              <Flex alignItems="center" gap="4" flexWrap="wrap">
                <Text fontSize="sm" fontWeight="bold">
                  Access Point Status:
                </Text>
                {status ? <Badge colorScheme={statusColorScheme[status]}>{status}</Badge> : null}
                {status === 'CONFIRMED' && !isActive ? <Badge colorScheme="red">Inaktiv</Badge> : null}
              </Flex>
              <Flex alignItems="center" gap="4" flexWrap="wrap">
                <Button size="sm" colorScheme="brand" onClick={onOpen} mt="3">
                  Status ändern
                </Button>
                {accessPointID && isActive ? (
                  <Button size="sm" colorScheme="red" onClick={() => handleOpenToggleModal(false)} mt="3">
                    Access Point pausieren
                  </Button>
                ) : null}
                {accessPointID && !isActive ? (
                  <Button size="sm" colorScheme="green" onClick={() => handleOpenToggleModal(true)} mt="3">
                    Access Point aktivieren
                  </Button>
                ) : null}
                {accessPointID ? (
                  <Button size="sm" colorScheme="orange" onClick={handleHideInventoryInApp} mt="3">
                    Inventar ausblenden
                  </Button>
                ) : null}
                {accessPointID ? (
                  <Button size="sm" colorScheme="green" onClick={handleUnhideInventoryInApp} mt="3">
                    Inventar einblenden
                  </Button>
                ) : null}
              </Flex>
              <Flex alignItems="center" gap="4" my="4">
                <Text fontSize="sm" fontWeight="bold">
                  {na1adlActive
                    ? `NA1ADL gestattet (seit ${moment(na1adlChangedAt || submitTimestamp).format('DD.MM.YYYY')})`
                    : `NA1ADL nicht gestattet (seit ${moment(na1adlChangedAt || submitTimestamp).format('DD.MM.YYYY')})`}
                </Text>
                <Tooltip label="Was ist NA1ADL: UPS Boten können Pakete von gescheiterten Zustellversuchen an DropPoints spontan umleiten. Das erhöht die Paketfrequenz beim DropPoint und optimiert so die Verdienstmöglichkeit. Das kann aber zu Übergabeversuchen ausserhalb der DropPoint-Öffnungszeiten führen.">
                  <Box height="16px" width="16px">
                    <Icon as={Question} />
                  </Box>
                </Tooltip>
              </Flex>
              {identificationSuccess ? (
                <>
                  <Text fontWeight="bold" fontSize="sm" mt="8">
                    Zeitpunkt der Freischaltung der UPS Features
                  </Text>
                  <Text>
                    {identificationSuccessTimestamp
                      ? moment(identificationSuccessTimestamp).format('DD.MM.YYYY HH:mm')
                      : 'Nicht freigeschaltet'}
                  </Text>
                </>
              ) : null}
              {rejectionTimestamp ? (
                <>
                  <Text fontWeight="bold" fontSize="sm" mt="4">
                    Zeitpunkt der Ablehnung der UPS Features
                  </Text>
                  <Text>{moment(rejectionTimestamp).format('DD.MM.YYYY HH:mm')}</Text>
                  <Text fontWeight="bold" fontSize="sm">
                    Grund der Ablehnung der UPS Features
                  </Text>
                  <Text>{rejectionReason}</Text>
                </>
              ) : null}
            </>
          ) : (
            <>
              <Text fontSize="sm" color="gray.500">
                Für diesen DropPoint ist noch kein UPS Access Point angelegt.
              </Text>
              <Button size="sm" colorScheme="brand" variant="outline" onClick={ensureDisclosure.onOpen} mt="3">
                {ensurePartnerAccessPointsLabel}
              </Button>
            </>
          )}
        </Box>
        <Box>
          <HubCounterPanel hubCounter={hubCounter} accessPoint={accessPoint} dropPointId={dropPointId} refetch={refetch} embedded />
        </Box>
      </Grid>

      {accessPoint ? (
        <ChangeAccessPointStatusModal
          isOpen={isOpen}
          onClose={onClose}
          refetch={refetch}
          dropPointId={dropPointId}
          accessPointID={accessPointID}
          identificationSuccess={identificationSuccess}
          status={status}
        />
      ) : null}
      {accessPointID ? (
        <ToggleAccessPointActiveStatusModal
          isOpen={isToggleOpen}
          onClose={onToggleClose}
          refetch={refetch}
          accessPointID={accessPointID}
          active={toggleTargetActive}
        />
      ) : null}
      {!accessPoint ? (
        <EnsurePartnerAccessPointsModal
          isOpen={ensureDisclosure.isOpen}
          onClose={ensureDisclosure.onClose}
          dropPointId={dropPointId}
          refetch={refetch}
        />
      ) : null}
    </>
  );
};

export default AccessPointTab;
